import React from 'react'
import { createSearchParams, useNavigate } from 'react-router-dom'

import { F0Box, F0Button, F0Heading, F0Text } from '@factorialco/f0-react'
import { Carousel } from '@factorialco/f0-react/dist/experimental'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { Activity } from 'modules/core/lib/activities/activity'
import type { ActivityComponent } from 'modules/core/lib/activities/types'
import i18n from 'modules/core/lib/i18n'
import {
  DeviceCatalogCartProvider,
  useDeviceCatalogCartContext,
} from 'modules/it_management/contexts/DeviceCatalogCartContext'

import { ActivityCartSidepanel } from './ActivityCartSidepanel'
import { RecommendationCard } from './RecommendationCard'
import type { DeviceRecommendationsData } from './types'

const DeviceRecommendationsContent: React.FC<DeviceRecommendationsData> = ({
  title,
  description,
  search,
  recommendations = [],
}) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const cart = useDeviceCatalogCartContext()

  const resolvedTitle = title ?? i18n.t('it_management.device_catalog.title')
  const normalizedSearch = search?.trim()

  React.useEffect(() => {
    const style = document.createElement('style')
    style.textContent = `
      [role="dialog"][data-state="open"] {
        min-width: 680px !important;
        width: 680px !important;
      }
    `
    document.head.appendChild(style)
    return () => {
      if (document.head.contains(style)) document.head.removeChild(style)
    }
  }, [])

  const openFullCatalog = () => {
    navigate({
      pathname: navigation.deviceCatalog.fullPath(),
      search: normalizedSearch ? createSearchParams({ search: normalizedSearch }).toString() : '',
      hash: '',
    })
  }

  return (
    <>
      <F0Box display="flex" flexDirection="column" gap="lg" padding="2xl">
        <F0Box display="flex" flexDirection="column" gap="xs">
          <F0Heading variant="heading" content={resolvedTitle} />
          {description && <F0Text variant="description" content={description} />}
        </F0Box>

        {recommendations.length > 0 ? (
          <Carousel
            showArrows
            showPeek
            columns={{
              default: 2,
              xs: 2,
              sm: 2,
              md: 2,
              lg: 3,
              xl: 3,
            }}
          >
            {recommendations.map((recommendation) => (
              <RecommendationCard key={recommendation.id} recommendation={recommendation} />
            ))}
          </Carousel>
        ) : (
          <F0Box display="flex" flexDirection="column" alignItems="center" padding="2xl" gap="sm">
            <F0Text
              variant="label"
              content={i18n.t('it_management.device_catalog.empty_state.title')}
            />
            <F0Text
              variant="description"
              content={i18n.t('it_management.device_catalog.empty_state.description')}
            />
          </F0Box>
        )}

        <F0Box display="flex" justifyContent="end">
          <F0Button
            label={i18n.t('it_management.device_catalog.order_plan.return_to_catalog')}
            onClick={openFullCatalog}
          />
        </F0Box>
      </F0Box>

      <ActivityCartSidepanel
        isOpen={cart.isOpen}
        items={cart.items}
        subtotal={cart.subtotal}
        total={cart.total}
        itemCount={cart.itemCount}
        onClose={cart.closeCart}
        onUpdateQuantity={cart.updateQuantity}
        onRemoveItem={cart.removeItem}
        onCheckout={openFullCatalog}
        isCheckingOut={false}
      />
    </>
  )
}

export const DeviceRecommendationsActivity: ActivityComponent<DeviceRecommendationsData> = (
  props
) => {
  return (
    <Activity>
      <DeviceCatalogCartProvider>
        <DeviceRecommendationsContent {...props} />
      </DeviceCatalogCartProvider>
    </Activity>
  )
}
